/**
 * chatHealthCounters.ts — what the chat can say about its own health, as
 * numbers (2026-09-28, Malaika's report: the chat froze while she typed, a
 * hard refresh did not help, and a send showed "couldn't start this reply").
 *
 * Until now nothing a browser saw reached us: no freeze, no failed Firestore
 * queue, no false watchdog card. Her day was reconstructed from server logs
 * and screenshots. This module counts what the browser sees; the beacon
 * (chatHealthBeacon.ts, loaded only for a signed-in person) sends the counts.
 *
 * What it keeps is COUNTS AND DURATIONS ONLY: how many long tasks and how
 * long, how slow an interaction was, how many sends the watchdog armed and
 * fired, how long a thread took to load, how full local storage is. Never a
 * message, a title, a file name, an address or an id of anything. The event
 * reasons are a closed list; nothing here accepts free text.
 *
 * A LEAF: it imports nothing, so the first download can carry it (it is
 * called from the Firestore boot and the self-heal, which are on it) and the
 * observers start before anything else can freeze the page.
 *
 * Owner decision (Miles, 2026-09-28): a health record for every signed-in
 * person, tagged with the person and the workspace, counts and timings only,
 * Cloud Logging only. A signed-out visitor's counts never leave the page.
 */

/** The closed list of events (server twin: functions/src/telemetry/chatHealthSchema.ts). */
export const CHAT_HEALTH_EVENT_REASONS = [
  'firestore-queue-failed',
  'firestore-self-heal',
  'firestore-heal-declined',
  'firestore-memory-cache',
  'firestore-freeze-fallback',
  'firestore-freeze-memory-cache',
  'storage-quota',
  'watchdog-fired',
  'send-withdrawn',
  'hydrate-timeout',
  'idb-blocked',
  'long-task-burst',
  'long-frame',
  'slow-interaction',
  'long-render',
  'rail-stalled',
] as const;
export type ChatHealthEventReason = typeof CHAT_HEALTH_EVENT_REASONS[number];

/** Events that are sent at once (at most one beacon a minute); the rest ride the summary. */
export const SEVERE_REASONS: ReadonlySet<ChatHealthEventReason> = new Set<ChatHealthEventReason>([
  'firestore-queue-failed',
  'firestore-heal-declined',
  'watchdog-fired',
  'hydrate-timeout',
  'long-task-burst',
  'rail-stalled',
]);

/** The send step the watchdog was on when it fired (server twin: SEND_PHASES). */
export type ChatHealthSendPhase = 'gate' | 'session' | 'last-stretch' | 'none';

/** The closed list of counters (server twin: COUNTER_KEYS). */
export const CHAT_HEALTH_COUNTER_KEYS = [
  'longTasks',
  'slowInteractions',
  'sendsArmed',
  'watchdogFires',
  'watchdogLateAcks',
  'sendsWithdrawn',
  'hydrates',
  'hydrateTimeouts',
  'idbOpens',
  'idbOpenTimeouts',
  'persists',
  'firestoreQueueFailures',
  'selfHeals',
  'storageQuotaErrors',
  'longTaskMsTotal',
  'longTaskMsMax',
  'interactionMsMax',
  'interactionMsP95',
  'hydrateMsMax',
  'idbOpenMsMax',
  'persistMsMax',
  'uptimeMs',
  'storageChars',
  'threadCacheChars',
  'domNodesMax',
  'transcriptRowsMax',
  'chatRenderMsMax',
] as const;
export type ChatHealthCounterKey = typeof CHAT_HEALTH_COUNTER_KEYS[number];
export type ChatHealthCounters = Partial<Record<ChatHealthCounterKey, number>>;

/**
 * How a long frame's longest script was started (the browser's own words,
 * server twin: FRAME_INVOKER_TYPES). Anything else is 'other'.
 */
export const FRAME_INVOKER_TYPES = [
  'classic-script',
  'module-script',
  'event-listener',
  'user-callback',
  'resolve-promise',
  'reject-promise',
  'other',
] as const;
export type ChatHealthFrameInvoker = typeof FRAME_INVOKER_TYPES[number];

/**
 * Where a long frame's time went (2026-09-29): the record showed a person's
 * page blocked for 15 s at a time and could not say by what. Chrome's
 * long-animation-frame entry names the script that ran; production publishes
 * its source maps, so a file and a character position are enough to find the
 * code offline. What is kept is OUR code's location and the browser's timing:
 * the file's name only when it is one of our own scripts (anything else is
 * 'other'), a position, a minified function name when it looks like one, the
 * invoker's TYPE from a closed list. Never the invoker itself (it can name an
 * element or an address), never a URL, never anything a person wrote.
 */
export interface ChatHealthFrame {
  ms: number;
  blockingMs: number;
  scriptMs: number;
  layoutMs: number;
  /** From the frame's rendering update to its end (animation callbacks, observers, style, layout, paint). */
  renderMs?: number;
  /** From the start of the frame's style and layout to its end. */
  styleLayoutMs?: number;
  invoker: ChatHealthFrameInvoker;
  file: string;
  at?: number;
  fn?: string;
  storage?: ChatHealthStorage;
  dom?: ChatHealthPageSize;
  chat?: ChatHealthChatRender;
}

/**
 * The chat's own renders during a frozen frame (2026-09-30): the record named
 * only the event that started a freeze (a focus, a scroll, React's scheduler),
 * never whether the time went to the chat re-rendering and how many message
 * rows it rebuilt. The chat host stamps each of its renders (from the first
 * line of its body to its last layout effect) with the rows it rebuilt; a long
 * frame carries the renders that overlapped it. Numbers only.
 */
export interface ChatHealthChatRender {
  /** Chat renders that overlapped the frame. */
  renders: number;
  /** Their time together, and the longest one. */
  ms: number;
  maxMs: number;
  /** Message rows they rebuilt together (a reused row is not counted). */
  rowsBuilt: number;
  /** Rows on screen in the largest of them. */
  rows: number;
  /**
   * The chat's own side work (its passive effects: saving, listening,
   * reading) that ran inside the frame, when any did (2026-10-01). A window
   * "focus" is a direct user action to React, so a state change in a focus
   * listener makes the chat update at once, effects included.
   */
  effectsMs?: number;
}

/**
 * How big the page was (2026-09-30): elements on the page, and transcript rows drawn (each row
 * carries data-msg-nav). The transcript opens expanded and a busy room reads its older messages
 * into the pane, so a thread of 800 messages is 800 rows the browser restyles and lays out.
 * Numbers only.
 */
export interface ChatHealthPageSize {
  nodes: number;
  rows: number;
}

/**
 * The stores of Firestore's on-device storage (@firebase/firestore's indexeddb_sentinels.ts,
 * the 18 Db…Store names; server twin: STORAGE_STORE_NAMES). A store not on the list is 'other'.
 */
export const STORAGE_STORE_NAMES = [
  'owner',
  'mutationQueues',
  'mutations',
  'documentMutations',
  'remoteDocumentsV14',
  'remoteDocumentGlobal',
  'targets',
  'targetDocuments',
  'targetGlobal',
  'collectionParents',
  'clientMetadata',
  'bundles',
  'namedQueries',
  'indexConfiguration',
  'indexState',
  'indexEntries',
  'documentOverlays',
  'globals',
  'other',
] as const;
export type ChatHealthStorageStore = typeof STORAGE_STORE_NAMES[number];
/** The kinds of storage request (IndexedDB's own method names; server twin: STORAGE_OPS). */
export const STORAGE_OPS = [
  'get',
  'getAll',
  'getAllKeys',
  'getKey',
  'put',
  'add',
  'delete',
  'clear',
  'count',
  'openCursor',
  'openKeyCursor',
  'other',
] as const;
export type ChatHealthStorageOp = typeof STORAGE_OPS[number];
/** A storage transaction's mode (server twin: STORAGE_TX_MODES). */
export const STORAGE_TX_MODES = ['readonly', 'readwrite', 'versionchange', 'other'] as const;
export type ChatHealthStorageTxMode = typeof STORAGE_TX_MODES[number];
/** At most this many kinds of request per storage callback (server twin: MAX_STORAGE_ISSUED). */
export const MAX_STORAGE_ISSUED = 6;

/** One kind of request a slow storage callback made while it ran, how often, and the time inside those calls. */
export interface ChatHealthStorageIssue {
  store: ChatHealthStorageStore;
  op: ChatHealthStorageOp;
  n: number;
  ms: number;
}

/**
 * What a frozen frame's storage callback was doing (2026-09-30, storageCallbackWatch.ts): how
 * long the callback itself ran, the request that had just finished, and the requests it made.
 * handlerMs 0 means the callback itself was quick: the time went to work it queued.
 */
export interface ChatHealthStorage {
  handlerMs: number;
  store?: ChatHealthStorageStore;
  op?: ChatHealthStorageOp;
  mode?: ChatHealthStorageTxMode;
  results?: number;
  issued?: ChatHealthStorageIssue[];
  issuedMs?: number;
}

/**
 * The reads a thread open makes (server twin: HYDRATE_READS). A slow open's
 * event names the ones still out (2026-09-30), never the thread.
 */
export const HYDRATE_READS = ['legacy', 'perDoc', 'overlay'] as const;
export type ChatHealthHydrateRead = typeof HYDRATE_READS[number];

/** What a slow interaction was (server twin: INTERACTION_TYPES): a key, a press, or anything else. */
export const INTERACTION_TYPES = ['key', 'pointer', 'other'] as const;
export type ChatHealthInteractionType = typeof INTERACTION_TYPES[number];

/**
 * Where a slow interaction's time went (2026-10-01): the record counted
 * Malaika's slow keys and clicks (p95 808 ms) and could not say why, because
 * only frames of 2 s or more carried any detail. The browser's own event
 * timing splits one interaction into three: the wait before our handlers ran
 * (something else held the page), our handlers, and the redraw after them.
 * Beside it, the long tasks and the chat's own renders inside it. Numbers and
 * a word from a closed list only: never the element, never a key.
 */
export interface ChatHealthInteraction {
  type: ChatHealthInteractionType;
  ms: number;
  /** From the input to the first handler: the page was busy with something else. */
  delayMs: number;
  /** The handlers themselves (React renders a key's update inside them). */
  handlerMs: number;
  /** From the last handler to the next paint: the browser's redraw. */
  paintMs: number;
  /** Long tasks inside the interaction (the page's own freezes). */
  longTaskMs: number;
  chat?: ChatHealthChatRender;
  dom?: ChatHealthPageSize;
}

/**
 * A chat render of 2 s or more (2026-10-01): one person's record held a render
 * of 14.4 s in the first 24 s of a page whose long tasks summed only 5 s, so
 * the render's start and end spanned time the page was not busy. This says
 * which: the long-task time inside it, and whether the page was hidden at any
 * point during it.
 */
export interface ChatHealthLongRender {
  ms: number;
  longTaskMs: number;
  hidden: boolean;
  rowsBuilt: number;
  rows: number;
}

/**
 * Which way Firestore failed (2026-10-01; server twin: FIRESTORE_FAILURE_KINDS). Malaika's record
 * held a queue failure 13 s into a page whose storage held 4,812 characters, so it was not the
 * full-storage kind, and the record could not say what it was. 'assertion' carries the SDK's own
 * assertion code (three or four hex digits, "(ID: b815)"); 'quota' is a full storage bin.
 */
export const FIRESTORE_FAILURE_KINDS = ['assertion', 'quota', 'other'] as const;
export type ChatHealthFirestoreFailureKind = typeof FIRESTORE_FAILURE_KINDS[number];
export interface ChatHealthFirestoreFailure {
  kind: ChatHealthFirestoreFailureKind;
  /** The SDK's assertion code, hex digits only (server twin: FIRESTORE_ASSERTION_ID_RE). */
  id?: string;
}
/** An assertion code as the SDK writes one (server twin: FIRESTORE_ASSERTION_ID_RE). */
export const FIRESTORE_ASSERTION_ID_RE = /^[0-9a-f]{3,4}$/;

/**
 * The failure's kind and code from what the page caught: the SDK's assertion code when the text
 * names one, and nothing else of the text (an assertion's context can name a document).
 */
export function firestoreFailureOf(cause: unknown): ChatHealthFirestoreFailure {
  let name = '';
  let text = '';
  try {
    if (typeof cause === 'string') text = cause;
    else if (cause && typeof cause === 'object') {
      name = String((cause as { name?: unknown }).name ?? '');
      text = String((cause as { message?: unknown }).message ?? '');
    }
  } catch { /* unreadable: 'other' */ }
  if (/INTERNAL ASSERTION FAILED/i.test(text)) {
    const m = /\(ID: ([0-9a-f]{3,4})\)/.exec(text);
    return m && FIRESTORE_ASSERTION_ID_RE.test(m[1]) ? { kind: 'assertion', id: m[1] } : { kind: 'assertion' };
  }
  if (name === 'QuotaExceededError' || /QuotaExceededError|exceeded the quota/i.test(text)) return { kind: 'quota' };
  return { kind: 'other' };
}

export interface ChatHealthEvent {
  reason: ChatHealthEventReason;
  atMs: number;
  send?: { phase: ChatHealthSendPhase; msInPhase: number; longTaskMsBeforeFire: number };
  frame?: ChatHealthFrame;
  hydrate?: { waiting: ChatHealthHydrateRead[] };
  interaction?: ChatHealthInteraction;
  render?: ChatHealthLongRender;
  firestore?: ChatHealthFirestoreFailure;
}

/** A long task at least this long is a freeze a person feels: sent at once. */
export const LONG_TASK_BURST_MS = 2_000;
/** An interaction (a key, a click) slower than this is counted as slow (the INP "poor" line is 500). */
export const SLOW_INTERACTION_MS = 200;
/** An interaction this slow is recorded with where its time went (the INP "poor" line is 500). */
export const SLOW_INTERACTION_DETAIL_MS = 400;
/** Chat renders of LONG_TASK_BURST_MS or more recorded with their detail, per page. */
export const MAX_LONG_RENDERS_PER_PAGE = 3;
/** Hidden stretches of the page kept for a long render to look up. */
const MAX_HIDDEN_SPANS = 20;
/** How far back the long tasks before a watchdog fire are summed. */
export const LONG_TASK_LOOKBACK_MS = 90_000;
/** Events kept waiting for the beacon (oldest dropped first). */
const MAX_PENDING_EVENTS = 20;
/** Interaction samples kept for the p95 (a reservoir, newest win). */
const MAX_INTERACTION_SAMPLES = 200;
/** The carry across a self-heal reload (sessionStorage, this tab only). */
export const CHAT_HEALTH_CARRY_KEY = 'prest0_chat_health_carry';
/** The prefix of the chat's cached transcripts (threadPersist.ts THREAD_MSG_KEY_PREFIX). */
const THREAD_CACHE_PREFIX = 'prest0_thread_msgs__';

const counts: Record<string, number> = {};
const pending: ChatHealthEvent[] = [];
const interactionSamples: number[] = [];
const recentLongTasks: Array<{ at: number; ms: number }> = [];
const sendPhases = new Map<string, { phase: ChatHealthSendPhase; since: number }>();
const listeners = new Set<(event: ChatHealthEvent) => void>();
let observersStarted = false;
let longRendersNoted = 0;
/** When the page was hidden, in the performance clock: [start, end], end Infinity while still hidden. */
const hiddenSpans: Array<{ start: number; end: number }> = [];
/** The page's observers, so a page that is being hidden can collect what they have not delivered yet. */
const observers: Array<{ observer: PerformanceObserver; handle: (entries: PerformanceEntryList) => void }> = [];
let version = 0;
let memoryCacheStart = false;

/** Whether this tab started Firestore on its in-memory cache (every beacon says so). */
export function startedOnMemoryCache(): boolean {
  return memoryCacheStart;
}

const clockNow = (): number => {
  try {
    return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
  } catch {
    return Date.now();
  }
};

const bump = (key: ChatHealthCounterKey, by = 1): void => {
  counts[key] = (counts[key] || 0) + by;
  version += 1;
};
const raiseMax = (key: ChatHealthCounterKey, value: number): void => {
  if (!Number.isFinite(value) || value < 0) return;
  if ((counts[key] || 0) < value) {
    counts[key] = value;
    version += 1;
  }
};

/** Count one occurrence. */
export function noteCount(key: 'sendsArmed' | 'watchdogLateAcks' | 'sendsWithdrawn' | 'storageQuotaErrors'): void {
  bump(key);
}

/** Record how long a measured step took (its count and its worst). */
export function noteTiming(what: 'hydrate' | 'idbOpen' | 'persist', ms: number): void {
  if (!Number.isFinite(ms) || ms < 0) return;
  if (what === 'hydrate') { bump('hydrates'); raiseMax('hydrateMsMax', ms); }
  else if (what === 'idbOpen') { bump('idbOpens'); raiseMax('idbOpenMsMax', ms); }
  else { bump('persists'); raiseMax('persistMsMax', ms); }
}

/** Long-task milliseconds in the window before `at` (the page's own freezes). */
export function longTaskMsSince(sinceMs: number): number {
  let total = 0;
  for (const t of recentLongTasks) if (t.at + t.ms >= sinceMs) total += t.ms;
  return Math.round(total);
}

/** Long-task milliseconds inside [startMs, endMs], each task clipped to the window. */
export function longTaskMsWithin(startMs: number, endMs: number): number {
  let total = 0;
  for (const t of recentLongTasks) {
    const from = Math.max(startMs, t.at);
    const to = Math.min(endMs, t.at + t.ms);
    if (to > from) total += to - from;
  }
  return Math.round(total);
}

/** The page went hidden (true) or visible again (false), at `at` on the performance clock. */
export function noteVisibility(hidden: boolean, at = clockNow()): void {
  const last = hiddenSpans[hiddenSpans.length - 1];
  if (hidden) {
    if (last && last.end === Infinity) return;
    hiddenSpans.push({ start: at, end: Infinity });
    if (hiddenSpans.length > MAX_HIDDEN_SPANS) hiddenSpans.shift();
  } else if (last && last.end === Infinity) {
    last.end = at;
  }
}

/** Whether the page was hidden at any moment inside [startMs, endMs]. */
export function wasHiddenWithin(startMs: number, endMs: number): boolean {
  return hiddenSpans.some((span) => span.start <= endMs && span.end >= startMs);
}

export function noteLongTask(ms: number, at = clockNow()): void {
  if (!Number.isFinite(ms) || ms <= 0) return;
  bump('longTasks');
  bump('longTaskMsTotal', ms);
  raiseMax('longTaskMsMax', ms);
  recentLongTasks.push({ at, ms });
  const cutoff = at - LONG_TASK_LOOKBACK_MS;
  while (recentLongTasks.length && recentLongTasks[0].at + recentLongTasks[0].ms < cutoff) recentLongTasks.shift();
  if (ms >= LONG_TASK_BURST_MS) noteEvent('long-task-burst');
}

export interface LongFrameLike {
  startTime?: number;
  duration?: number;
  blockingDuration?: number;
  renderStart?: number;
  styleAndLayoutStart?: number;
  scripts?: ReadonlyArray<{
    startTime?: number;
    duration?: number;
    forcedStyleAndLayoutDuration?: number;
    invoker?: string;
    invokerType?: string;
    sourceURL?: string;
    sourceCharPosition?: number;
    sourceFunctionName?: string;
  }>;
}

export type StorageCallbackLookup = (startMs: number, endMs: number) => ChatHealthStorage | null;
let storageLookup: StorageCallbackLookup | null = null;

/**
 * Where a frozen frame's storage callback is looked up (storageCallbackWatch's
 * storageCallbackDuring, wired by the Firestore boot; this module imports nothing).
 */
export function setStorageCallbackLookup(lookup: StorageCallbackLookup | null): void {
  storageLookup = lookup;
}

/** The lookup the Firestore boot set, for the frame detail (chatHealthDetail.ts) to ask. */
export function storageCallbackLookup(): StorageCallbackLookup | null {
  return storageLookup;
}

/** A chat render at least this long is kept for a frame to find (shorter ones cannot add up to a freeze worth naming). */
export const CHAT_RENDER_KEEP_MS = 16;
/** The chat renders kept, newest last. */
const MAX_CHAT_RENDERS = 40;
const chatRenders: Array<{ start: number; end: number; rowsBuilt: number; rows: number }> = [];
/** The chat's effect runs kept, newest last (the same floor and cap as its renders). */
const chatEffectRuns: Array<{ start: number; end: number }> = [];

/** The clock chat renders are stamped with: the one long-animation-frame entries use. */
export function chatRenderClock(): number {
  try {
    return typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : 0;
  } catch {
    return 0;
  }
}

/**
 * One chat render, from the first line of the host's body to its last layout
 * effect, with the rows it rebuilt (null when its message rows were not built
 * again). Cheap: a comparison, and a push when the render took 16 ms or more.
 */
export function noteChatRender(startMs: number, endMs: number, rowBuild: { built: number; rows: number } | null): void {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs || startMs <= 0) return;
  const ms = endMs - startMs;
  raiseMax('chatRenderMsMax', Math.round(ms));
  if (ms < CHAT_RENDER_KEEP_MS) return;
  const rowsBuilt = rowBuild && rowBuild.built > 0 ? rowBuild.built : 0;
  const rows = rowBuild && rowBuild.rows > 0 ? rowBuild.rows : 0;
  chatRenders.push({ start: startMs, end: endMs, rowsBuilt, rows });
  if (chatRenders.length > MAX_CHAT_RENDERS) chatRenders.splice(0, chatRenders.length - MAX_CHAT_RENDERS);
  if (ms < LONG_TASK_BURST_MS || longRendersNoted >= MAX_LONG_RENDERS_PER_PAGE) return;
  longRendersNoted += 1;
  noteEvent('long-render', undefined, undefined, undefined, {
    render: {
      ms: Math.round(ms),
      longTaskMs: longTaskMsWithin(startMs, endMs),
      hidden: wasHiddenWithin(startMs, endMs),
      rowsBuilt,
      rows,
    },
  });
}

/**
 * One run of the chat's own passive effects: from its first-declared effect to
 * its last (all of them belong to the chat's one component, hooks included, and
 * run in declaration order). Kept when it took 16 ms or more.
 */
export function noteChatEffects(startMs: number, endMs: number): void {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs || startMs <= 0) return;
  if (endMs - startMs < CHAT_RENDER_KEEP_MS) return;
  chatEffectRuns.push({ start: startMs, end: endMs });
  if (chatEffectRuns.length > MAX_CHAT_RENDERS) chatEffectRuns.splice(0, chatEffectRuns.length - MAX_CHAT_RENDERS);
}

/** The page's size now: its elements and the transcript rows drawn. Null without a document. */
export function pageSize(): ChatHealthPageSize | null {
  try {
    if (typeof document === 'undefined') return null;
    const size = {
      nodes: document.getElementsByTagName('*').length,
      rows: document.querySelectorAll('[data-msg-nav]').length,
    };
    raiseMax('domNodesMax', size.nodes);
    raiseMax('transcriptRowsMax', size.rows);
    return size;
  } catch {
    return null;
  }
}

export function noteInteraction(ms: number): void {
  if (!Number.isFinite(ms) || ms < 0) return;
  raiseMax('interactionMsMax', ms);
  if (ms >= SLOW_INTERACTION_MS) bump('slowInteractions');
  interactionSamples.push(ms);
  if (interactionSamples.length > MAX_INTERACTION_SAMPLES) interactionSamples.shift();
  version += 1;
}

/** The chat's kept renders and effect runs, newest last (read by the frame detail; never written there). */
export function chatRenderLog(): { renders: ReadonlyArray<{ start: number; end: number; rowsBuilt: number; rows: number }>; effects: ReadonlyArray<{ start: number; end: number }> } {
  return { renders: chatRenders, effects: chatEffectRuns };
}

/** The browser's event-timing entry, as far as the record reads it. */
export interface InteractionEntryLike {
  name?: string;
  startTime?: number;
  duration?: number;
  processingStart?: number;
  processingEnd?: number;
  interactionId?: number;
}

/** What the detail (chatHealthDetail.ts) is handed: slow interactions, and frames of LONG_TASK_BURST_MS or more. */
export interface ChatHealthDetailHooks {
  slowInteractions(entries: ReadonlyArray<InteractionEntryLike>): void;
  longFrame(entry: LongFrameLike, pageOrigin: string, dom: ChatHealthPageSize | null): void;
}
let detailHooks: ChatHealthDetailHooks | null = null;
/** What arrived before the detail was installed (the beacon loads after the page), oldest dropped. */
const earlySlowEntries: InteractionEntryLike[] = [];
const earlyLongFrames: Array<{ entry: LongFrameLike; origin: string; dom: ChatHealthPageSize | null }> = [];
const MAX_EARLY_ENTRIES = 24;

/**
 * Where a slow interaction or a long frame spent its time is worked out by chatHealthDetail.ts, which
 * rides the beacon's own download (a signed-in person's only): this leaf rides the first download, so
 * it only hands entries over. Until the beacon installs the detail, they wait here; a long frame keeps
 * the page's size as it was then.
 */
export function setChatHealthDetail(hooks: ChatHealthDetailHooks | null): void {
  detailHooks = hooks;
  if (!hooks) return;
  if (earlySlowEntries.length) hooks.slowInteractions(earlySlowEntries.splice(0));
  for (const f of earlyLongFrames.splice(0)) hooks.longFrame(f.entry, f.origin, f.dom);
}

function handleSlowEntries(entries: ReadonlyArray<InteractionEntryLike>): void {
  const slow = entries.filter((e) => e.interactionId && typeof e.duration === 'number' && e.duration >= SLOW_INTERACTION_DETAIL_MS);
  if (!slow.length) return;
  if (detailHooks) { detailHooks.slowInteractions(slow); return; }
  earlySlowEntries.push(...slow);
  if (earlySlowEntries.length > MAX_EARLY_ENTRIES) earlySlowEntries.splice(0, earlySlowEntries.length - MAX_EARLY_ENTRIES);
}

function handleLongFrames(entries: ReadonlyArray<LongFrameLike>, origin: string): void {
  for (const entry of entries) {
    if (!(typeof entry.duration === 'number' && entry.duration >= LONG_TASK_BURST_MS)) continue;
    const dom = pageSize();
    if (detailHooks) detailHooks.longFrame(entry, origin, dom);
    else if (earlyLongFrames.push({ entry, origin, dom }) > MAX_EARLY_ENTRIES) earlyLongFrames.shift();
  }
}

/** The send step a dispatch is on (the watchdog's steps, useGlobalChat's sendDoorFor). */
export function noteSendPhase(clientTaskId: string, phase: ChatHealthSendPhase): void {
  if (!clientTaskId) return;
  sendPhases.set(clientTaskId, { phase, since: clockNow() });
  if (sendPhases.size > 50) {
    const oldest = sendPhases.keys().next().value;
    if (oldest !== undefined) sendPhases.delete(oldest);
  }
}

/** Forget a send's step (it left, or it ended). */
export function clearSendPhase(clientTaskId: string): void {
  if (clientTaskId) sendPhases.delete(clientTaskId);
}

/**
 * The watchdog fired for a send: counted, and an event carrying the step it
 * was on, how long it had been there, and how much of that time the page
 * itself was frozen (a busy main thread looks exactly like a stuck step).
 */
export function noteWatchdogFired(clientTaskId?: string): void {
  bump('watchdogFires');
  const now = clockNow();
  const at = clientTaskId ? sendPhases.get(clientTaskId) : undefined;
  const since = at ? at.since : now - LONG_TASK_LOOKBACK_MS;
  noteEvent('watchdog-fired', {
    phase: at ? at.phase : 'none',
    msInPhase: Math.max(0, Math.round(now - since)),
    longTaskMsBeforeFire: longTaskMsSince(since),
  });
}

/** Record an event from the closed list; severe ones reach the beacon at once. */
export function noteEvent(
  reason: ChatHealthEventReason,
  send?: ChatHealthEvent['send'],
  frame?: ChatHealthFrame,
  hydrate?: ChatHealthEvent['hydrate'],
  detail?: Pick<ChatHealthEvent, 'interaction' | 'render' | 'firestore'>,
): void {
  if (!(CHAT_HEALTH_EVENT_REASONS as readonly string[]).includes(reason)) return;
  if (reason === 'firestore-queue-failed') bump('firestoreQueueFailures');
  if (reason === 'firestore-self-heal') bump('selfHeals');
  if (reason === 'hydrate-timeout') bump('hydrateTimeouts');
  if (reason === 'idb-blocked') bump('idbOpenTimeouts');
  if (reason === 'storage-quota') bump('storageQuotaErrors');
  if (reason === 'send-withdrawn') bump('sendsWithdrawn');
  if (reason === 'firestore-memory-cache' || reason === 'firestore-freeze-memory-cache') memoryCacheStart = true;
  const event: ChatHealthEvent = {
    reason,
    atMs: Date.now(),
    ...(reason === 'watchdog-fired' && send ? { send } : {}),
    ...(reason === 'long-frame' && frame ? { frame } : {}),
    ...(reason === 'hydrate-timeout' && hydrate ? { hydrate } : {}),
    ...(reason === 'slow-interaction' && detail?.interaction ? { interaction: detail.interaction } : {}),
    ...(reason === 'long-render' && detail?.render ? { render: detail.render } : {}),
    ...(reason === 'firestore-queue-failed' && detail?.firestore ? { firestore: detail.firestore } : {}),
  };
  pending.push(event);
  if (pending.length > MAX_PENDING_EVENTS) pending.shift();
  version += 1;
  listeners.forEach((listener) => {
    try { listener(event); } catch { /* a listener never breaks the page */ }
  });
}

/** Called for every new event (the beacon's hook for sending severe ones at once). */
export function onChatHealthEvent(listener: (event: ChatHealthEvent) => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** How many events are waiting for the beacon. */
export function pendingEventCount(): number {
  return pending.length;
}

/** Take the waiting events (the beacon sends them). */
export function takePendingEvents(): ChatHealthEvent[] {
  return pending.splice(0, pending.length);
}

/** Put events back after a send that could not leave (kept, oldest dropped past the cap). */
export function returnPendingEvents(events: ChatHealthEvent[]): void {
  pending.unshift(...events);
  while (pending.length > MAX_PENDING_EVENTS) pending.shift();
}

/** Changes whenever a counter or event changes: the beacon sends a summary only after one. */
export function chatHealthVersion(): number {
  return version;
}

function p95(samples: number[]): number | undefined {
  if (samples.length < 5) return undefined;
  const sorted = [...samples].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))];
}

/** Local storage size, in characters (the browser's quota is counted in them). */
export function scanStorageChars(): { storageChars: number; threadCacheChars: number } | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    let storageChars = 0;
    let threadCacheChars = 0;
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key === null) continue;
      const size = key.length + (localStorage.getItem(key)?.length ?? 0);
      storageChars += size;
      if (key.startsWith(THREAD_CACHE_PREFIX)) threadCacheChars += size;
    }
    return { storageChars, threadCacheChars };
  } catch {
    return null;
  }
}

/** The counters as numbers, rounded (every key from the closed list, only those). */
export function chatHealthSnapshot(): ChatHealthCounters {
  const out: ChatHealthCounters = {};
  for (const key of CHAT_HEALTH_COUNTER_KEYS) {
    const value = counts[key];
    if (typeof value === 'number' && Number.isFinite(value)) out[key] = Math.round(value);
  }
  const p = p95(interactionSamples);
  if (p !== undefined) out.interactionMsP95 = Math.round(p);
  const size = pageSize();
  if (size) {
    out.domNodesMax = Math.round(counts.domNodesMax ?? size.nodes);
    out.transcriptRowsMax = Math.round(counts.transcriptRowsMax ?? size.rows);
  }
  out.uptimeMs = Math.round(clockNow());
  const storage = scanStorageChars();
  if (storage) {
    out.storageChars = storage.storageChars;
    out.threadCacheChars = storage.threadCacheChars;
  }
  return out;
}

/**
 * Before a self-heal reload: keep this tab's waiting events in sessionStorage
 * so the next page can send them (the heal is exactly what we most need to
 * hear about, and the reload would lose it).
 */
export function carryAcrossReload(): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    const events = pending.slice(-MAX_PENDING_EVENTS);
    sessionStorage.setItem(CHAT_HEALTH_CARRY_KEY, JSON.stringify({ events }));
  } catch { /* a full or blocked store loses the carry, never the page */ }
}

function restoreCarry(): void {
  try {
    if (typeof sessionStorage === 'undefined') return;
    const raw = sessionStorage.getItem(CHAT_HEALTH_CARRY_KEY);
    if (!raw) return;
    sessionStorage.removeItem(CHAT_HEALTH_CARRY_KEY);
    const parsed = JSON.parse(raw) as { events?: unknown };
    if (!Array.isArray(parsed.events)) return;
    for (const e of parsed.events.slice(-MAX_PENDING_EVENTS)) {
      const reason = (e as { reason?: unknown })?.reason;
      const atMs = (e as { atMs?: unknown })?.atMs;
      if (typeof reason !== 'string' || !(CHAT_HEALTH_EVENT_REASONS as readonly string[]).includes(reason)) continue;
      const event: ChatHealthEvent = { reason: reason as ChatHealthEventReason, atMs: typeof atMs === 'number' ? atMs : Date.now() };
      // The heal's reload carries the failure that caused it: keep which failure, from the closed list only.
      const failure = (e as { firestore?: { kind?: unknown; id?: unknown } })?.firestore;
      if (reason === 'firestore-queue-failed' && failure && (FIRESTORE_FAILURE_KINDS as readonly unknown[]).includes(failure.kind)) {
        event.firestore = { kind: failure.kind as ChatHealthFirestoreFailureKind };
        if (failure.kind === 'assertion' && typeof failure.id === 'string' && FIRESTORE_ASSERTION_ID_RE.test(failure.id)) event.firestore.id = failure.id;
      }
      pending.push(event);
    }
    version += 1;
  } catch { /* a malformed carry is dropped */ }
}

/**
 * Start watching the page (once): long tasks and slow interactions through
 * PerformanceObserver (durations only; the entries' targets and names are
 * never read), long animation frames (handed to the detail, which keeps the
 * location of their longest script in our own code), and restore a carry
 * from a self-heal reload. Never throws.
 */
export function startChatHealthObservers(): void {
  if (observersStarted) return;
  observersStarted = true;
  restoreCarry();
  startVisibilityWatch();
  try {
    if (typeof PerformanceObserver === 'undefined') return;
    const supported = (PerformanceObserver as unknown as { supportedEntryTypes?: readonly string[] }).supportedEntryTypes ?? [];
    if (supported.includes('longtask')) {
      watch('longtask', { type: 'longtask', buffered: true }, (entries) => {
        for (const entry of entries) noteLongTask(entry.duration, entry.startTime);
      });
    }
    if (supported.includes('event')) {
      watch('event', { type: 'event', buffered: true, durationThreshold: 40 } as PerformanceObserverInit, (entries) => {
        for (const entry of entries) {
          // One interaction may produce several entries (keydown, keyup…); only those with an id are a person's.
          if ((entry as unknown as { interactionId?: number }).interactionId) noteInteraction(entry.duration);
        }
        handleSlowEntries(entries as unknown as InteractionEntryLike[]);
      });
    }
    if (supported.includes('long-animation-frame') && typeof window !== 'undefined' && window.location) {
      const origin = window.location.origin;
      watch('long-animation-frame', { type: 'long-animation-frame', buffered: true }, (entries) => {
        handleLongFrames(entries as unknown as LongFrameLike[], origin);
      });
    }
  } catch { /* an old browser simply reports less */ }
}

/** One observer, kept so a page being hidden can collect its undelivered entries. */
function watch(_type: string, init: PerformanceObserverInit, handle: (entries: PerformanceEntryList) => void): void {
  const observer = new PerformanceObserver((list) => handle(list.getEntries()));
  observer.observe(init);
  observers.push({ observer, handle });
}

/**
 * Hand the observers' undelivered entries to the record now (2026-10-01). The
 * browser delivers performance entries in a later task, so a freeze that made
 * someone leave the tab reached the page-hide summary without its own entries:
 * one person's record held a 14.4 s chat render and no long task over 3.2 s.
 * The beacon calls this before every summary. Never throws.
 */
export function drainChatHealthObservers(): void {
  for (const { observer, handle } of observers) {
    try {
      const entries = observer.takeRecords();
      if (entries.length) handle(entries);
    } catch { /* an observer that cannot be read keeps its entries for its own callback */ }
  }
}

function startVisibilityWatch(): void {
  try {
    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') return;
    if (document.visibilityState === 'hidden') noteVisibility(true, 0);
    document.addEventListener('visibilitychange', () => {
      noteVisibility(document.visibilityState === 'hidden');
    });
  } catch { /* the record simply cannot say whether a page was hidden */ }
}

/** Tests only: back to a fresh page. */
export function resetChatHealthForTests(): void {
  for (const k of Object.keys(counts)) delete counts[k];
  pending.length = 0;
  interactionSamples.length = 0;
  recentLongTasks.length = 0;
  sendPhases.clear();
  listeners.clear();
  observersStarted = false;
  version = 0;
  memoryCacheStart = false;
  storageLookup = null;
  chatRenders.length = 0;
  chatEffectRuns.length = 0;
  longRendersNoted = 0;
  detailHooks = null;
  earlySlowEntries.length = 0;
  earlyLongFrames.length = 0;
  hiddenSpans.length = 0;
  observers.length = 0;
}
